import { escapeRegExp } from "lodash";
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useTransition,
} from "react";
import { InputActionMeta } from "react-select";
import useEvent from "react-use-event-hook";
import { useRecoilValue } from "recoil";

import {
  useDynamicFormsUtils,
  usePageContext,
  useSelectItemsTranslate,
  useTableFiltersActions,
} from "@ryde/redux";
import {
  IDropdownItem,
  ISelectListItem,
  Log,
  NO_VALUE_DISPLAY,
  normalizeToArrayValue,
  sortFilterDropdownArray,
  tableRowsAtomFamily,
  ValueType,
} from "@ryde/shared";

import { IFilterMultiProps } from "../parts/filter-multiselect-models";

export const useFilterMultiselectUnbound = (props: IFilterMultiProps) => {
  const { customFieldId, isServerSearch, initialCheckedValues } = props;
  const [isOpen, setIsOpen] = useState(false);
  const { setValuesToFilter } = useTableFiltersActions();
  const { entity } = usePageContext();
  const rows = useRecoilValue(tableRowsAtomFamily(entity));
  const [dropdownItems, setDropdownItems] = useState<ISelectListItem[]>([]);
  const {
    getControlSelectItems,
    getGridControlLabelById,
    getGridFieldNameByControlId,
  } = useDynamicFormsUtils();
  const [input, setInput] = useState("");
  const [, startTransition] = useTransition();

  const [dropdownValue, setDropdownValue] = useState<ISelectListItem[]>([]);
  const { getTranslatedSelectList } = useSelectItemsTranslate();

  const gridFieldName = useMemo(
    () =>
      customFieldId ? getGridFieldNameByControlId(customFieldId) : undefined,
    [customFieldId, getGridFieldNameByControlId],
  );
  const valueValuesSet = useMemo(() => {
    return new Set(dropdownValue.map(({ value }) => value));
  }, [dropdownValue]);

  const dropdownSourceEntityName = useMemo(() => {
    if (!customFieldId) {
      return undefined;
    }

    const rowItems = getControlSelectItems(customFieldId);

    if (!rowItems) {
      return undefined;
    }

    return getTranslatedSelectList(rowItems);
  }, [customFieldId, getControlSelectItems, getTranslatedSelectList]);

  const multiselectLabel = useMemo((): string => {
    if (customFieldId) {
      return getGridControlLabelById(customFieldId);
    }

    return "";
  }, [customFieldId, getGridControlLabelById]);

  const onItemsCheckChange = useCallback(
    (newValue: IDropdownItem[]): void => {
      if (newValue && customFieldId) {
        Log.debug("Update filter value for field: " + customFieldId, newValue);
        setDropdownValue(newValue as IDropdownItem[]);
        setValuesToFilter?.(
          customFieldId,
          (newValue?.map(item => item.value) || []) as number[] | string[],
        );
      }
    },
    [setValuesToFilter, customFieldId],
  );

  const inputToFilter = useMemo(() => input.toLowerCase().trim(), [input]);
  const filteredDropDownItems = useMemo(
    () =>
      inputToFilter
        ? dropdownItems.filter(({ label }) =>
            String(label).toLowerCase().match(escapeRegExp(inputToFilter)),
          )
        : dropdownItems,
    [dropdownItems, inputToFilter],
  );

  const isSelectedAll = filteredDropDownItems?.every(({ value }) =>
    valueValuesSet.has(value),
  );

  const selectAll = useEvent((): void => {
    if (!customFieldId) {
      return;
    }

    const filteredDropDownItemsValues = filteredDropDownItems.map(
      ({ value }) => value,
    );

    const includedFilteredDropdownItems = [
      ...dropdownValue,
      ...filteredDropDownItems,
    ];
    const filteredDropDownItemsValuesSet = new Set(filteredDropDownItemsValues);
    const excludedFilteredDropdownItems = dropdownValue.filter(
      ({ value }) => !filteredDropDownItemsValuesSet.has(value),
    );
    const includedFilteredDropdownItemsValues =
      includedFilteredDropdownItems.map(({ value }) => value);
    const excludedFilteredDropdownItemsValues =
      excludedFilteredDropdownItems.map(({ value }) => value);
    setDropdownValue(
      !isSelectedAll
        ? includedFilteredDropdownItems
        : excludedFilteredDropdownItems,
    );
    setValuesToFilter(
      customFieldId,
      !isSelectedAll
        ? (includedFilteredDropdownItemsValues as number[] | string[])
        : (excludedFilteredDropdownItemsValues as number[] | string[]),
    );
  });

  const getDropdownItemsFromRows = useCallback((): IDropdownItem[] => {
    if (!gridFieldName) {
      return [];
    }

    const set = new Set<string>();

    rows?.forEach(row => {
      const filterValue = row[gridFieldName];

      if (filterValue) {
        set.add(filterValue);
      }
    });
    return Array.from(set).map(rowValue => ({
      value: rowValue,
      label: rowValue,
    }));
  }, [gridFieldName, rows]);

  const loadFilterDropdownItems = useCallback(async () => {
    if (dropdownSourceEntityName) {
      setDropdownItems(dropdownSourceEntityName || []);
    } else if (!isServerSearch) {
      const items = getDropdownItemsFromRows();
      setDropdownItems(items || []);
    }
  }, [dropdownSourceEntityName, getDropdownItemsFromRows, isServerSearch]);

  useEffect(() => {
    if (!isOpen) {
      setDropdownItems(items => sortFilterDropdownArray(items, dropdownValue));
    }

    /* sort items - put selected items on top while reopening dropdown (but not on selecting each item)*/
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const setInitialDropdownValues = useCallback(
    (items: ISelectListItem[], filterIds?: ValueType | null) => {
      if (filterIds && customFieldId) {
        const initialDropdownValueItems = items.filter(v =>
          normalizeToArrayValue(filterIds).some(
            x => String(x) === String(v.value),
          ),
        );
        Log.debug(
          "Set initial value for filter field: " + customFieldId,
          initialDropdownValueItems,
          filterIds,
        );
        setDropdownValue(initialDropdownValueItems);
      } else if (filterIds === null) {
        setDropdownValue([]);
      }
    },
    [customFieldId],
  );
  useEffect(() => {
    if (customFieldId) {
      startTransition(() => {
        loadFilterDropdownItems();
      });
    }
  }, [customFieldId, loadFilterDropdownItems]);

  useEffect(() => {
    startTransition(() => {
      setInitialDropdownValues(dropdownItems, initialCheckedValues);
    });
  }, [dropdownItems, initialCheckedValues, setInitialDropdownValues]);

  const buttonContent = useMemo(
    () => ({
      labelBlack: `${
        dropdownValue.length
          ? dropdownItems.find(item => item.value === dropdownValue[0].value)
              ?.label
          : NO_VALUE_DISPLAY
      }`,
      labelBlue: `${dropdownValue.length > 1 ? `+${dropdownValue.length - 1}` : ""}`,
    }),
    [dropdownValue, dropdownItems],
  );

  const onInputChange = useEvent(
    (inputValue: string, action: InputActionMeta): void => {
      if (action.action === "input-change") {
        setInput(inputValue);
      }
    },
  );

  return {
    isOpen,
    setIsOpen,
    value: dropdownValue,
    buttonContent,
    multiselectLabel,
    onItemsCheckChange,
    dropdownItems,
    selectAll,
    isSelectedAll,
    input,
    onInputChange,
    filteredDropDownItems,
  };
};
